import { useState } from "react";
import AppUtils from "../Utils/AppUtils";
import * as Sentry from "@sentry/browser";

const useAPIs = () => {
  const [apiError, setError] = useState(null);
  const [processing, setProcessing] = useState(false);

  const handleError = (error, onFailure, response, inputData) => {
    setProcessing(false);
    onFailure && onFailure(error);
    AppUtils.showToast(error ? error : "Something went wrong!", "error");
    setError(error);

    // Customize Sentry error capture with additional data
    Sentry.captureException(error, {
      tags: { endpoint: response?.url },
      extra: {
        payload: JSON.stringify(inputData ?? {}),
        response: {
          status: response?.status,
          headers: response?.headers,
          data: response?.data,
        },
      },
    });
  };

  const handleCatch = (error, onFailure) => {
    setProcessing(false);
    onFailure && onFailure(error);
    console.log(error, "handleCatch");
    Sentry.captureException(error);
    setError(error);
  };

  const postData = (url, inputData, onSuccess, onFailure) => {
    try {
      setError(null);
      setProcessing(true);
      fetch(process.env.REACT_APP_BASE_URL.concat(url), {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "x-auth": sessionStorage.getItem("x-auth"),
        },
        body: JSON.stringify(inputData ?? {}),
      })
        .then((res) => {
          return res.json().then((data) => ({ response: res, data }));
        })
        .then(({ response, data }) => {
          setProcessing(false);

          // Access the specific response header
          const xAuthHeader = response.headers.get("x-auth");
          if (xAuthHeader) {
            sessionStorage.setItem("x-auth", xAuthHeader);
          }
          if (data.status === "success" || data.status === 200) {
            onSuccess && onSuccess(data.data, data);
            return;
          } else {
            handleError(data?.message, onFailure, response, inputData);
            return;
          }
        })
        .catch((error) => {
          handleError(error, onFailure);
        });
    } catch (error) {
      handleCatch(error, onFailure);
    }
  };

  const postFormData = (url, inputData, onSuccess, onFailure) => {
    try {
      setError(null);
      setProcessing(true);
      fetch(process.env.REACT_APP_BASE_URL.concat(url), {
        method: "POST",
        headers: {
          "x-auth": sessionStorage.getItem("x-auth"),
        },
        body: inputData,
      })
        .then((res) => res.json().then((data) => ({ response: res, data })))
        .then(({ response, data }) => {
          setProcessing(false);
          if (data && data.status === 200) {
            onSuccess && onSuccess(data.data);
            return;
          } else {
            handleError(data?.message, onFailure, response, inputData);
            return;
          }
        })
        .catch((error) => {
          handleError(error, onFailure);
        });
    } catch (error) {
      handleCatch(error, onFailure);
    }
  };

  const getData = (url, onSuccess, onFailure) => {
    try {
      setError(null);
      setProcessing(true);
      fetch(process.env.REACT_APP_BASE_URL.concat(url), {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "x-auth": sessionStorage.getItem("x-auth"),
        },
      })
        .then((res) => res.json())
        .then((data) => {
          setProcessing(false);
          if (data && data.status === 200) {
            onSuccess && onSuccess(data.data);
            return;
          } else {
            handleError(data?.message, onFailure);
            return;
          }
        })
        .catch((error) => {
          handleError(error, onFailure);
        });
    } catch (error) {
      handleCatch(error, onFailure);
    }
  };

  const getExternalData = async (url, onSuccess, onFailure) => {
    try {
      setError(null);
      setProcessing(true);

      const res = await fetch(url, { method: "GET" });
      console.log("HTTP Response:", res);

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const data = await res.json();
      console.log("Parsed Data:", data);
      setProcessing(false);

      if (data && res.status === 200) {
        if (onSuccess) onSuccess(data);
      } else {
        handleError(data?.message || "Unknown error", onFailure);
      }
    } catch (error) {
      setProcessing(false);
      handleError(error.message || "Fetch error", onFailure);
    }
  };

  const downloadFile = async (fileUrl, onSuccess, onFailure) => {
    try {
      // Retrieve the token from sessionStorage
      const authToken = sessionStorage.getItem("x-auth");
      if (!authToken) {
        AppUtils.showToast("Authentication token not found in sessionStorage.", "error");
        return;
      }
      const url = process.env.REACT_APP_BASE_URL.concat(fileUrl);
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "X-Auth": authToken,
        },
      });

      if (response.ok) {
        const blob = await response.blob();

        // Extract filename from Content-Disposition header
        const contentDispositionHeader = response.headers.get("Content-Disposition");
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(contentDispositionHeader);
        let filename = "downloaded_file"; // Default filename
        if (matches && matches[1]) {
          filename = matches[1].replace(/['"]/g, ""); // Remove quotes if present
        }

        // Create a temporary anchor element to trigger the download
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = filename;
        document.body.appendChild(link);

        // Trigger the download
        link.click();

        // Remove the temporary anchor element
        document.body.removeChild(link);
        onSuccess && onSuccess(response);
      } else {
        AppUtils.showToast(`Failed to download the file. HTTP status: ${response.status}`, "error");
        onFailure && onFailure(response.data);
        // console.error("Failed to download the file. HTTP status:", response.status);
      }
    } catch (error) {
      AppUtils.showToast(`Error downloading the file: ${error}`, "error");
      // console.error("Error downloading the file:", error);
    }
  };

  return {
    apiError,
    processing,
    postData,
    postFormData,
    getData,
    getExternalData,
    downloadFile,
  };
};

export default useAPIs;
